import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import { Box } from '@mantine/core';

import { reportEvent } from '~/domains/analytics';
import { Routes } from '~/domains/common/constants/routes';
import PopularPicksSection from '~/domains/contest/domains/quickPick/components/Board/PopularPicksSection/PopularPicksSection';
import useStoredPicks from '~/domains/contest/domains/quickPick/components/Board/PicksDrawer/useStoredPicks';
import {
  reportQuickPicksPickMake,
  reportQuickPicksPlayerCardView,
} from '~/domains/contest/domains/quickPick/analyticsEvents';
import type { PropPickProps } from '~/domains/contest/domains/common/types/propPickProps.type';
import type { PlayerCellProps } from '~/domains/contest/domains/quickPick/components/Board/PlayerCell/PlayerCell';

import { useHomeQuickPicks } from '../hooks/useHomeQuickPicks';

function HomeQuickpicks({ order }: { order: number }) {
  const router = useRouter();
  const { getStoredPicks } = useStoredPicks();

  const { popularProps, isLoading, error } = useHomeQuickPicks();

  // -------------- HANDLERS --------------
  const handleMakePick = useCallback(
    ({ direction, ...pick }: PropPickProps) => {
      const hasPicks = getStoredPicks();
      const parsedDirection = direction === 'over' ? 'o' : 'u';
      const selectedPick = [pick.id, parsedDirection].join('-');

      reportQuickPicksPickMake({ location: 'homePromo' });

      // Do not pass props if there are already picks stored
      router.push(Routes.quickPicksBoard({ props: hasPicks ? undefined : selectedPick }));
    },
    [router, getStoredPicks]
  );

  const handleCardClick = useCallback(
    (id: string) => {
      reportQuickPicksPlayerCardView({ location: 'homePromo' });
      router.push(Routes.quickPicksBoard({ playerCardId: id }));
    },
    [router]
  );

  const items = useMemo(
    (): PlayerCellProps[] =>
      popularProps.map((pick) => ({
        ...pick,
        onPick: handleMakePick,
        onHeaderClick: handleCardClick,
      })),
    [popularProps, handleMakePick, handleCardClick]
  );

  // Promo widget: hide entirely when there is nothing to show or on error.
  if (!isLoading && (error || !items.length)) return null;

  return (
    <Box component="section" style={{ order }}>
      <PopularPicksSection
        items={items}
        isLoading={isLoading}
        fullWidth
        hideTeamLogo
        onViewAll={() => reportEvent('Home > View More - QuickPicks')}
      />
    </Box>
  );
}

export default HomeQuickpicks;
